import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../utils/supabase";
import {
    load,
    save,
    buildHistoryFromWorkoutRows,
    getValidWorkoutDatesFromHistory,
} from "../utils/helpers";
import { loadPersistentDeletedDates, removeHistoryDateFromMap } from "../utils/appHelpers";
import { logWriteTrace } from "../utils/writeTrace";

// ─── Constants ───────────────────────────────────────────────────────────────
const REMOTE_HISTORY_SESSION_LOOKBACK_DAYS = 180;

// History browsing (calendar, home, history tab) is unlimited for all users.
// The clamp function is kept as a no-op so call sites don't need to change.
// AI context history is still limited per-user tier in useAI.js separately.
const clampStartForFreeTier = (dateKey, _isPro) => dateKey;
const REMOTE_HISTORY_SESSION_LIMIT = 400;
const INITIAL_HOME_HISTORY_LOOKBACK_DAYS = 30;
const INITIAL_HOME_HISTORY_LIMIT = 80;
const HISTORY_RECOVERY_LIMIT = 250;

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useHistorySync({
    user,
    isPro = false,
    latestHistoryRef,
    workoutsDataHistoryRef,
    workoutsDataHistory,
    setHistory,
    setWorkoutsDataHistory,
    applyWorkoutsDataHistorySnapshot,
    historyDeleteMarkersRef,
    getCurrentHistoryDeleteMarkers,
    historyReloadNonce,
    persistHistoryForUser,
    screen,
    sessionSyncVersion,
    applyLocalHistoryDates,
    pendingWorkoutContentChangeDatesRef,
    onStartupUnsyncedLocalDates = null,
    // module-level helpers from App.jsx passed as params
    shouldLogPerfDebug,
    getRuntimeEnvironmentLabel,
    isTransientSupabaseFetchError,
    logRecordFetchError,
    attachRecordFetchContext,
    classifyNetworkError,
    getCurrentWeekRangeForHomeSummary,
    getHistoryOverallMetrics,
    getHistoryMetricsForDate,
    getWorkoutRowsDebugSummary,
    getWorkoutSessionRowsDebugSummary,
    getNextMonthPrefix,
    getHomeWeeklySummaryDebug,
    getHomeWeeklySourceDebug,
    getDateDaysAgoKey,
    isPlainObject,
    serializeHistoryMap,
    buildHistoryFromWorkoutSessionRows,
    buildRemoteHistoryWithWorkoutRowsPriority,
    normalizeTrustedWorkoutRowDate,
    normalizeTrustedSessionRowDate,
    sortTrustedRowsByDate,
    mergeTrustedRowsByDate,
    serializeTrustedRows,
    serializeTrustedRow,
    buildTrustedRowSignatureMap,
    applyHistoryDeleteMarkers,
    loadTrustedHistoryCache,
    buildVersionedHistoryCache,
    getUserHistoryCacheKey,
    HISTORY_OWNER_KEY,
    getHistoryDeleteMarkersKey,
    normalizeHistoryDeleteMarkers,
    createEmptyHistoryDeleteMarkers,
}) {
    // ─── State ────────────────────────────────────────────────────────────────
    const [trustedWorkoutRows, setTrustedWorkoutRows] = useState([]);
    const [trustedSessionRows, setTrustedSessionRows] = useState([]);
    const [historySyncReady, setHistorySyncReady] = useState(false);
    const [historyRemoteReady, setHistoryRemoteReady] = useState(false);
    const [historyLoadError, setHistoryLoadError] = useState("");
    // True when the initial Supabase fetch failed and we are displaying from the
    // localStorage cache instead.  Distinct from historyRemoteReady (which stays
    // false) so display gates can allow rendering without trusting remote data.
    const [historyDisplayFromCache, setHistoryDisplayFromCache] = useState(false);

    // ─── Refs ─────────────────────────────────────────────────────────────────
    const historyRemoteLoadFailedRef = useRef(false);
    const trustedWorkoutRowsRef = useRef(trustedWorkoutRows);
    const trustedSessionRowsRef = useRef(trustedSessionRows);
    const trustedWorkoutRowSignaturesRef = useRef(buildTrustedRowSignatureMap(trustedWorkoutRows, normalizeTrustedWorkoutRowDate));
    const trustedSessionRowSignaturesRef = useRef(buildTrustedRowSignatureMap(trustedSessionRows, normalizeTrustedSessionRowDate));
    const trustedRowsOwnerRef = useRef(null);
    const displayHistoryRefreshRequestIdRef = useRef(0);
    const homeWeeklySummaryRequestIdRef = useRef(0);
    const historyHasTrustedRemoteSnapshotRef = useRef(false);
    const supabaseFetchInFlightRef = useRef(new Map());
    const supabaseFetchBackoffRef = useRef({});
    const supabaseFetchFreshUntilRef = useRef({});
    const startupReconcileForUserRef = useRef(null);
    // Tracks the earliest date that has been authoritatively loaded from Supabase
    // into in-memory history (updated after initial_load and display_history_refresh).
    // Used by persistHistoryForUserBounded in App.jsx to inject rangeFrom for all writes.
    const historyLoadedRangeStartRef = useRef(null);

    // ─── Callbacks ────────────────────────────────────────────────────────────

    const applyTrustedWorkoutRowsSnapshot = useCallback((rows = [], {
        replace = false,
        source = "workouts.data",
        skipPendingFilter = false,
    } = {}) => {
        const deletedDates = new Set(historyDeleteMarkersRef.current?.dates || []);
        const incomingRows = (rows || [])
            .filter((row) => normalizeTrustedWorkoutRowDate(row))
            .filter((row) => !deletedDates.has(normalizeTrustedWorkoutRowDate(row)));

        // P2: 同期待ちの日付を background fetch で上書きしない。
        // skipPendingFilter:true は sync verify や内部状態管理の呼び出し元が指定する。
        const pendingDates = !skipPendingFilter && pendingWorkoutContentChangeDatesRef?.current
            ? new Set(pendingWorkoutContentChangeDatesRef.current.keys())
            : new Set();
        const filteredIncomingRows = pendingDates.size > 0
            ? incomingRows.filter((row) => {
                const date = normalizeTrustedWorkoutRowDate(row);
                return !date || !pendingDates.has(date);
            })
            : incomingRows;

        // マーカーログ: 削除マーカーで弾いた場合のみ出力
        const _blockedIncomingDates = deletedDates.size > 0
            ? (rows || [])
                .map((row) => normalizeTrustedWorkoutRowDate(row))
                .filter((d) => d && deletedDates.has(d))
            : [];
        const _blockedExistingDates = deletedDates.size > 0
            ? (trustedWorkoutRowsRef.current || [])
                .map((row) => normalizeTrustedWorkoutRowDate(row))
                .filter((d) => d && deletedDates.has(d))
            : [];
        if (_blockedIncomingDates.length > 0 || _blockedExistingDates.length > 0) {
            console.log("[WP] trustedRows:markerApplied", {
                incomingCount: (rows || []).length,
                filteredCount: incomingRows.length,
                blockedDates: [...new Set([..._blockedIncomingDates, ..._blockedExistingDates])],
            });
        }

        // マージ時に既存行にも削除マーカーを適用する。
        // replace: false のとき mergeTrustedRowsByDate は currentRows を上書きしないため、
        // deletedDates に含まれる既存行が nextRows に残ってしまうのを防ぐ。
        const currentRows = (trustedWorkoutRowsRef.current || [])
            .filter((row) => !deletedDates.has(normalizeTrustedWorkoutRowDate(row)));
        const hasBlockedExisting = currentRows.length < (trustedWorkoutRowsRef.current || []).length;

        if (!replace) {
            const hasIncomingChange = filteredIncomingRows.some((row) => {
                const date = normalizeTrustedWorkoutRowDate(row);
                return trustedWorkoutRowSignaturesRef.current.get(date) !== serializeTrustedRow(row, normalizeTrustedWorkoutRowDate);
            });
            if (!hasIncomingChange && !hasBlockedExisting) {
                if (shouldLogPerfDebug()) {
                    const previousSignature = serializeTrustedRows(trustedWorkoutRowsRef.current || [], normalizeTrustedWorkoutRowDate);
                    console.log("[history] trusted row cache update", {
                        action: "trusted_row_cache_update",
                        source,
                        rowsCount: filteredIncomingRows.length,
                        rowsHash: previousSignature,
                        previousRowsHash: previousSignature,
                        skippedSameSnapshot: true,
                        stateUpdated: false,
                    });
                }
                return false;
            }
        }
        const nextRows = replace
            ? sortTrustedRowsByDate(filteredIncomingRows, normalizeTrustedWorkoutRowDate)
            : mergeTrustedRowsByDate(currentRows, filteredIncomingRows, normalizeTrustedWorkoutRowDate);
        const previousSignature = serializeTrustedRows(trustedWorkoutRowsRef.current || [], normalizeTrustedWorkoutRowDate);
        const nextSignature = serializeTrustedRows(nextRows, normalizeTrustedWorkoutRowDate);
        if (previousSignature === nextSignature) {
            if (shouldLogPerfDebug()) {
                console.log("[history] trusted row cache update", {
                    action: "trusted_row_cache_update",
                    source,
                    rowsCount: nextRows.length,
                    rowsHash: nextSignature,
                    previousRowsHash: previousSignature,
                    skippedSameSnapshot: true,
                    stateUpdated: false,
                });
            }
            return false;
        }
        trustedWorkoutRowsRef.current = nextRows;
        trustedWorkoutRowSignaturesRef.current = buildTrustedRowSignatureMap(nextRows, normalizeTrustedWorkoutRowDate);
        setTrustedWorkoutRows(nextRows);
        if (shouldLogPerfDebug()) {
            console.log("[history] trusted row cache update", {
                action: "trusted_row_cache_update",
                source,
                replace,
                rowsCount: nextRows.length,
                rowsHash: nextSignature,
                previousRowsHash: previousSignature,
                skippedSameSnapshot: false,
                stateUpdated: true,
                dates: nextRows.map(normalizeTrustedWorkoutRowDate),
            });
        }
        return true;
    }, [
        historyDeleteMarkersRef,
        normalizeTrustedWorkoutRowDate,
        pendingWorkoutContentChangeDatesRef,
        serializeTrustedRow,
        shouldLogPerfDebug,
        serializeTrustedRows,
        sortTrustedRowsByDate,
        mergeTrustedRowsByDate,
        buildTrustedRowSignatureMap,
    ]);

    const applyTrustedSessionRowsSnapshot = useCallback((rows = [], {
        replace = false,
        source = "workout_sessions.summary_json",
    } = {}) => {
        const incomingRows = (rows || []).filter((row) => normalizeTrustedSessionRowDate(row));
        if (!replace) {
            const hasIncomingChange = incomingRows.some((row) => {
                const date = normalizeTrustedSessionRowDate(row);
                return trustedSessionRowSignaturesRef.current.get(date) !== serializeTrustedRow(row, normalizeTrustedSessionRowDate);
            });
            if (!hasIncomingChange) {
                if (shouldLogPerfDebug()) {
                    const previousSignature = serializeTrustedRows(trustedSessionRowsRef.current || [], normalizeTrustedSessionRowDate);
                    console.log("[history] trusted row cache update", {
                        action: "trusted_row_cache_update",
                        source,
                        rowsCount: incomingRows.length,
                        rowsHash: previousSignature,
                        previousRowsHash: previousSignature,
                        skippedSameSnapshot: true,
                        stateUpdated: false,
                    });
                }
                return false;
            }
        }
        const nextRows = replace
            ? sortTrustedRowsByDate(incomingRows, normalizeTrustedSessionRowDate)
            : mergeTrustedRowsByDate(trustedSessionRowsRef.current || [], incomingRows, normalizeTrustedSessionRowDate);
        const previousSignature = serializeTrustedRows(trustedSessionRowsRef.current || [], normalizeTrustedSessionRowDate);
        const nextSignature = serializeTrustedRows(nextRows, normalizeTrustedSessionRowDate);
        if (previousSignature === nextSignature) {
            if (shouldLogPerfDebug()) {
                console.log("[history] trusted row cache update", {
                    action: "trusted_row_cache_update",
                    source,
                    rowsCount: nextRows.length,
                    rowsHash: nextSignature,
                    previousRowsHash: previousSignature,
                    skippedSameSnapshot: true,
                    stateUpdated: false,
                });
            }
            return false;
        }
        trustedSessionRowsRef.current = nextRows;
        trustedSessionRowSignaturesRef.current = buildTrustedRowSignatureMap(nextRows, normalizeTrustedSessionRowDate);
        setTrustedSessionRows(nextRows);
        if (shouldLogPerfDebug()) {
            console.log("[history] trusted row cache update", {
                action: "trusted_row_cache_update",
                source,
                replace,
                rowsCount: nextRows.length,
                rowsHash: nextSignature,
                previousRowsHash: previousSignature,
                skippedSameSnapshot: false,
                stateUpdated: true,
                dates: nextRows.map(normalizeTrustedSessionRowDate),
            });
        }
        return true;
    }, [
        normalizeTrustedSessionRowDate,
        serializeTrustedRow,
        shouldLogPerfDebug,
        serializeTrustedRows,
        sortTrustedRowsByDate,
        mergeTrustedRowsByDate,
        buildTrustedRowSignatureMap,
    ]);

    const markSupabaseFetchFresh = useCallback((key, ttlMs = 30000) => {
        if (!key) return;
        supabaseFetchFreshUntilRef.current[key] = Date.now() + ttlMs;
    }, []);

    const isSupabaseFetchFresh = useCallback((key) => {
        if (!key) return false;
        return Number(supabaseFetchFreshUntilRef.current[key] || 0) > Date.now();
    }, []);

    // Invalidate all freshness entries whose key starts with `prefix`.
    // Does NOT trigger a fetch — the next runDedupeSupabaseFetch call for a
    // matching key will simply skip the "fresh" guard and proceed normally.
    const invalidateSupabaseFetchFreshByPrefix = useCallback((prefix) => {
        if (!prefix) return [];
        const invalidated = [];
        for (const key of Object.keys(supabaseFetchFreshUntilRef.current)) {
            if (key.startsWith(prefix)) {
                delete supabaseFetchFreshUntilRef.current[key];
                invalidated.push(key);
            }
        }
        return invalidated;
    }, []);

    const runDedupeSupabaseFetch = useCallback(async (
        key,
        fetcher,
        {
            minIntervalMs = 15000,
            freshTtlMs = 30000,
            backoffMs = 30000,
            context = {},
        } = {}
    ) => {
        const now = Date.now();
        const nextAllowedAt = Number(supabaseFetchBackoffRef.current[key]?.nextAllowedAt || 0);

        const inFlightRequest = supabaseFetchInFlightRef.current.get(key);
        if (inFlightRequest) {
            console.warn("[supabase fetch] dedupe in-flight attach", {
                env: getRuntimeEnvironmentLabel(),
                key,
                applied: false,
                reason: "same request already in flight; waiting for shared result",
                ...context,
            });
            const sharedResult = await inFlightRequest;
            return {
                ...sharedResult,
                deduped: true,
                reason: sharedResult?.reason || "shared-in-flight",
            };
        }

        if (nextAllowedAt > now) {
            console.warn("[supabase fetch] backoff skip", {
                env: getRuntimeEnvironmentLabel(),
                key,
                applied: false,
                reason: "transient error backoff",
                retryAfterMs: nextAllowedAt - now,
                ...context,
            });
            return { skipped: true, reason: "backoff", retryAfterMs: nextAllowedAt - now };
        }

        if (isSupabaseFetchFresh(key)) {
            console.log("[supabase fetch] fresh skip", {
                env: getRuntimeEnvironmentLabel(),
                key,
                applied: false,
                reason: "fresh identical range already fetched",
                ...context,
            });
            return { skipped: true, reason: "fresh" };
        }

        const fetchName = context.fetchName || key;
        const startedAt = Date.now();
        const fetchPromise = (async () => {
            console.log(`[home fetch] ${fetchName} start`, {
                env: getRuntimeEnvironmentLabel(),
                key,
                user_id: context.user_id || context.userId || null,
                dateRange: context.dateRange || null,
                table: context.table || null,
                tables: context.tables || null,
            });
            const value = await fetcher();
            markSupabaseFetchFresh(key, freshTtlMs || minIntervalMs);
            delete supabaseFetchBackoffRef.current[key];
            const rowsCount =
                Array.isArray(value?.data)
                    ? value.data.length
                    : Object.entries(value || {}).reduce((acc, [name, result]) => {
                        if (Array.isArray(result?.data)) acc[name] = result.data.length;
                        return acc;
                    }, {});
            console.log(`[home fetch] ${fetchName} end`, {
                env: getRuntimeEnvironmentLabel(),
                key,
                durationMs: Date.now() - startedAt,
                rowsCount,
                skipped: false,
                applied: true,
                reason: "success",
                user_id: context.user_id || context.userId || null,
                dateRange: context.dateRange || null,
            });
            return { skipped: false, value, reason: "success" };
        })();

        supabaseFetchInFlightRef.current.set(key, fetchPromise);

        try {
            return await fetchPromise;
        } catch (error) {
            console.warn(`[home fetch] ${fetchName} failed`, {
                env: getRuntimeEnvironmentLabel(),
                key,
                durationMs: Date.now() - startedAt,
                user_id: context.user_id || context.userId || null,
                dateRange: context.dateRange || null,
                message: error?.message || String(error || "unknown error"),
                code: error?.code || null,
                status: error?.status || null,
            });
            if (isTransientSupabaseFetchError(error)) {
                const previousBackoffMs = Number(supabaseFetchBackoffRef.current[key]?.backoffMs || backoffMs);
                const nextBackoffMs = Math.min(Math.max(backoffMs, previousBackoffMs * 2), 120000);
                supabaseFetchBackoffRef.current[key] = {
                    nextAllowedAt: Date.now() + nextBackoffMs,
                    backoffMs: nextBackoffMs,
                };
                console.warn("[supabase fetch] transient error backoff set", {
                    env: getRuntimeEnvironmentLabel(),
                    key,
                    nextBackoffMs,
                    message: error?.message || String(error || "unknown error"),
                    code: error?.code || null,
                    status: error?.status || null,
                    ...context,
                });
            }
            throw error;
        } finally {
            if (supabaseFetchInFlightRef.current.get(key) === fetchPromise) {
                supabaseFetchInFlightRef.current.delete(key);
            }
        }
    }, [isSupabaseFetchFresh, markSupabaseFetchFresh, getRuntimeEnvironmentLabel, isTransientSupabaseFetchError]);

    // ─── User owner reset effect ───────────────────────────────────────────────
    useEffect(() => {
        const nextOwner = user?.id || null;
        if (trustedRowsOwnerRef.current === nextOwner) return;
        trustedRowsOwnerRef.current = nextOwner;
        trustedWorkoutRowsRef.current = [];
        trustedSessionRowsRef.current = [];
        trustedWorkoutRowSignaturesRef.current = new Map();
        trustedSessionRowSignaturesRef.current = new Map();
        setTrustedWorkoutRows([]);
        setTrustedSessionRows([]);
    }, [user?.id]);

    // ─── syncHistoryFromSupabase effect ───────────────────────────────────────
    useEffect(() => {
        let isActive = true;

        const syncHistoryFromSupabase = async () => {
            if (!user?.id) {
                historyRemoteLoadFailedRef.current = false;
                historyHasTrustedRemoteSnapshotRef.current = false;
                historyDeleteMarkersRef.current = createEmptyHistoryDeleteMarkers();
                historyLoadedRangeStartRef.current = null;
                if (isActive) setHistoryLoadError("");
                if (isActive) setHistoryRemoteReady(false);
                if (isActive) setHistoryDisplayFromCache(false);
                if (isActive) setHistorySyncReady(true);
                return;
            }

            if (isActive) {
                const currentDisplayedMetrics = getHistoryOverallMetrics(latestHistoryRef.current || {});
                const currentWorkoutsDataMetrics = getHistoryOverallMetrics(workoutsDataHistoryRef.current || {});
                const hasCurrentTrustedDisplay =
                    currentDisplayedMetrics.setCount > 0 ||
                    currentWorkoutsDataMetrics.setCount > 0;
                setHistorySyncReady(false);
                setHistoryRemoteReady(hasCurrentTrustedDisplay);
                setHistoryLoadError("");
                // Keep historyDisplayFromCache as-is during re-fetch so the user
                // continues to see cached data while the new Supabase request is in flight.
                if (!historyHasTrustedRemoteSnapshotRef.current) {
                    console.log("[restore] ignore localStorage bootstrap until Supabase history load succeeds", {
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        currentDisplayed: currentDisplayedMetrics,
                        currentWorkoutsDataDisplayed: currentWorkoutsDataMetrics,
                        historyRemoteReady: hasCurrentTrustedDisplay,
                        cachedHistory: getHistoryOverallMetrics(
                            loadTrustedHistoryCache(getUserHistoryCacheKey(user.id), loadTrustedHistoryCache("history", {}))
                        ),
                    });
                    if (!hasCurrentTrustedDisplay) {
                        setHistory({});
                        workoutsDataHistoryRef.current = {};
                        setWorkoutsDataHistory({});
                    }
                }
            }

            const rawLocalHistory = loadTrustedHistoryCache("history", {});
            const localOwnerUserId = load(HISTORY_OWNER_KEY, null);
            const scopedLocalHistory = loadTrustedHistoryCache(getUserHistoryCacheKey(user.id), null);
            const persistedDeleteMarkers = normalizeHistoryDeleteMarkers(
                load(getHistoryDeleteMarkersKey(user.id), createEmptyHistoryDeleteMarkers())
            );
            historyDeleteMarkersRef.current = persistedDeleteMarkers;

            let localMergeCandidate = {};

            if (isPlainObject(scopedLocalHistory)) {
                localMergeCandidate = scopedLocalHistory;
            } else if (!localOwnerUserId || localOwnerUserId === user.id) {
                localMergeCandidate = rawLocalHistory;
            } else {
                save(getUserHistoryCacheKey(localOwnerUserId), buildVersionedHistoryCache(rawLocalHistory));
            }

            const effectiveDeleteMarkers = getCurrentHistoryDeleteMarkers();

            try {
                // Early offline detection: if the OS already reports no network
                // connectivity, skip the Supabase fetch entirely and fall through
                // to the catch block immediately instead of waiting ~21 s for a
                // timeout.  navigator.onLine being true does NOT mean the host is
                // reachable (it only means the device has *some* network), so we
                // still attempt the real fetch when true.
                if (typeof navigator !== "undefined" && navigator.onLine === false) {
                    throw Object.assign(
                        new Error("navigator.onLine is false — network unavailable"),
                        { __offlineEarlyExit: true }
                    );
                }
                const sessionRangeStart = clampStartForFreeTier(
                    getDateDaysAgoKey(INITIAL_HOME_HISTORY_LOOKBACK_DAYS),
                    isPro
                );
                const initialHistoryLimit = INITIAL_HOME_HISTORY_LIMIT;
                const initialFetchKey = `history_initial_load:${user.id}:${sessionRangeStart}:${initialHistoryLimit}`;
                const initialFetch = await runDedupeSupabaseFetch(
                    initialFetchKey,
                    async () => {
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => {
                            controller.abort();
                        }, 5000);
                        try {
                        const initialWeekRange = getCurrentWeekRangeForHomeSummary();
                        const workoutsRes = await supabase
                            .from("workouts")
                            .select("date, data")
                            .eq("user_id", user.id)
                            .gte("date", sessionRangeStart)
                            .order("date", { ascending: false })
                            .limit(initialHistoryLimit)
                            .abortSignal(controller.signal);
                        if (workoutsRes.error) {
                            const context = logRecordFetchError("history_initial_load", "workouts", workoutsRes.error, {
                                userId: user.id,
                                dateRange: { from: sessionRangeStart, limit: initialHistoryLimit },
                                query: "workouts.select(date,data).eq(user_id).gte(date).order(date desc).limit",
                                responseData: workoutsRes.data,
                            });
                            throw attachRecordFetchContext(workoutsRes.error, context);
                        }
                        const weekWorkoutsRes = await supabase
                            .from("workouts")
                            .select("date, data")
                            .eq("user_id", user.id)
                            .gte("date", initialWeekRange.start)
                            .lte("date", initialWeekRange.end)
                            .order("date", { ascending: true })
                            .limit(14)
                            .abortSignal(controller.signal);
                        if (weekWorkoutsRes.error) {
                            const context = logRecordFetchError("history_initial_load_week", "workouts", weekWorkoutsRes.error, {
                                userId: user.id,
                                dateRange: { from: initialWeekRange.start, to: initialWeekRange.end },
                                query: "workouts.select(date,data).eq(user_id).gte(date).lte(date).order(date asc)",
                                responseData: weekWorkoutsRes.data,
                            });
                            throw attachRecordFetchContext(weekWorkoutsRes.error, context);
                        }
                        const combinedWorkoutRows = [
                            ...(workoutsRes.data || []),
                            ...(weekWorkoutsRes.data || []),
                        ].sort((a, b) => String(a?.date || "").localeCompare(String(b?.date || "")));
                        return {
                            workoutsRes: {
                                ...workoutsRes,
                                data: combinedWorkoutRows,
                                initialRowsCount: workoutsRes.data?.length || 0,
                                weekRowsCount: weekWorkoutsRes.data?.length || 0,
                                weekRange: initialWeekRange,
                            },
                            sessionsRes: {
                                data: [],
                                error: null,
                                skipped: true,
                                skippedReason: "home initial load uses workouts.data only",
                            },
                        };
                        } finally {
                            clearTimeout(timeoutId);
                        }
                    },
                    {
                        minIntervalMs: 20000,
                        freshTtlMs: 45000,
                        backoffMs: 30000,
                        context: {
                            fetchName: "history_initial_load",
                            user_id: user.id,
                            dateRange: {
                                from: sessionRangeStart,
                                limit: initialHistoryLimit,
                                week: getCurrentWeekRangeForHomeSummary(),
                            },
                            tables: ["workouts"],
                        },
                    }
                );

                if (initialFetch.skipped) {
                    const currentDisplayed = latestHistoryRef.current || {};
                    const currentWorkoutsData = workoutsDataHistoryRef.current || {};
                    const currentDisplayedMetrics = getHistoryOverallMetrics(currentDisplayed);
                    const currentWorkoutsDataMetrics = getHistoryOverallMetrics(currentWorkoutsData);
                    const hasCurrentTrustedDisplay =
                        currentDisplayedMetrics.setCount > 0 ||
                        currentWorkoutsDataMetrics.setCount > 0;
                    console.warn("[restore] history initial load skipped by fetch guard", {
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        dateRange: { from: sessionRangeStart, limit: initialHistoryLimit },
                        reason: initialFetch.reason,
                        fallbackUsed: false,
                        currentDisplayed: currentDisplayedMetrics,
                        currentWorkoutsDataDisplayed: currentWorkoutsDataMetrics,
                        historyRemoteReady: hasCurrentTrustedDisplay,
                        remoteLoadFailed: historyRemoteLoadFailedRef.current,
                    });
                    if (isActive && hasCurrentTrustedDisplay) {
                        historyRemoteLoadFailedRef.current = false;
                        historyHasTrustedRemoteSnapshotRef.current = true;
                        setHistoryRemoteReady(true);
                        setHistoryLoadError("");
                    }
                    return;
                }

                let { workoutsRes, sessionsRes } = initialFetch.value;

                if (sessionsRes.error) {
                    logRecordFetchError("history_initial_load", "workout_sessions", sessionsRes.error, {
                        userId: user.id,
                        dateRange: { from: sessionRangeStart, limit: initialHistoryLimit },
                        query: "workout_sessions.select(workout_date,duration_sec,summary_json).eq(user_id).gte(workout_date).order(workout_date asc).limit",
                        responseData: sessionsRes.data,
                    });
                    console.warn("[records] workout_sessions history load failed; continuing with workouts data", {
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        table: "workout_sessions",
                        query: "workout_sessions.select(workout_date,duration_sec,summary_json).eq(user_id).gte(workout_date).order(workout_date asc).limit",
                        code: sessionsRes.error?.code,
                        message: sessionsRes.error?.message,
                        details: sessionsRes.error?.details,
                        hint: sessionsRes.error?.hint,
                        responseData: sessionsRes.data,
                    });
                }

                console.log("[restore] history initial load response", {
                    env: getRuntimeEnvironmentLabel(),
                    fetchName: "history_initial_load",
                    user_id: user.id,
                    dateRange: { from: sessionRangeStart, limit: initialHistoryLimit },
                    initialRowsCount: workoutsRes.initialRowsCount ?? null,
                    weekRowsCount: workoutsRes.weekRowsCount ?? null,
                    weekRange: workoutsRes.weekRange || null,
                    workouts: getWorkoutRowsDebugSummary(workoutsRes.data || []),
                    workout_sessions: sessionsRes.error
                        ? { rowCount: 0, error: sessionsRes.error?.message || String(sessionsRes.error) }
                        : sessionsRes.skipped
                            ? { rowCount: 0, skipped: true, reason: sessionsRes.skippedReason }
                            : getWorkoutSessionRowsDebugSummary(sessionsRes.data || []),
                    error: {
                        workouts: workoutsRes.error?.message || null,
                        workout_sessions: sessionsRes.error?.message || null,
                    },
                });

                if (!(workoutsRes.data || []).length && !(sessionsRes.error ? [] : (sessionsRes.data || [])).length) {
                    const recoveryMinDate = clampStartForFreeTier("0000-01-01", isPro);
                    const recoveryFetchKey = `history_recovery_latest:${user.id}:${HISTORY_RECOVERY_LIMIT}:${recoveryMinDate}`;
                    const recoveryFetch = await runDedupeSupabaseFetch(
                        recoveryFetchKey,
                        async () => {
                            let recoveryWorkoutsQuery = supabase
                                .from("workouts")
                                .select("date, data")
                                .eq("user_id", user.id)
                                .order("date", { ascending: false })
                                .limit(HISTORY_RECOVERY_LIMIT);
                            let recoverySessionsQuery = supabase
                                .from("workout_sessions")
                                .select("workout_date, duration_sec, total_volume, exercise_count, summary_json")
                                .eq("user_id", user.id)
                                .order("workout_date", { ascending: false })
                                .limit(HISTORY_RECOVERY_LIMIT);
                            const [recoveryWorkoutsRes, recoverySessionsRes] = await Promise.all([
                                recoveryWorkoutsQuery,
                                recoverySessionsQuery,
                            ]);

                            if (recoveryWorkoutsRes.error) {
                                const context = logRecordFetchError("history_recovery_latest", "workouts", recoveryWorkoutsRes.error, {
                                    userId: user.id,
                                    dateRange: { latest: true, limit: HISTORY_RECOVERY_LIMIT },
                                    query: "workouts.select(date,data).eq(user_id).order(date desc).limit",
                                    responseData: recoveryWorkoutsRes.data,
                                });
                                throw attachRecordFetchContext(recoveryWorkoutsRes.error, context);
                            }

                            return {
                                workoutsRes: {
                                    ...recoveryWorkoutsRes,
                                    data: [...(recoveryWorkoutsRes.data || [])].reverse(),
                                },
                                sessionsRes: {
                                    ...recoverySessionsRes,
                                    data: [...(recoverySessionsRes.data || [])].reverse(),
                                },
                            };
                        },
                        {
                            minIntervalMs: 20000,
                            freshTtlMs: 45000,
                            backoffMs: 30000,
                            context: {
                                fetchName: "history_recovery_latest",
                                user_id: user.id,
                                dateRange: { latest: true, limit: HISTORY_RECOVERY_LIMIT },
                                tables: ["workouts", "workout_sessions"],
                            },
                        }
                    );

                    if (!recoveryFetch.skipped) {
                        workoutsRes = recoveryFetch.value.workoutsRes;
                        sessionsRes = recoveryFetch.value.sessionsRes;
                        console.log("[restore] history recovery latest response", {
                            env: getRuntimeEnvironmentLabel(),
                            fetchName: "history_recovery_latest",
                            user_id: user.id,
                            dateRange: { latest: true, limit: HISTORY_RECOVERY_LIMIT },
                            workouts: getWorkoutRowsDebugSummary(workoutsRes.data || []),
                            workout_sessions: sessionsRes.error
                                ? { rowCount: 0, error: sessionsRes.error?.message || String(sessionsRes.error) }
                                : getWorkoutSessionRowsDebugSummary(sessionsRes.data || []),
                            error: {
                                workouts: workoutsRes.error?.message || null,
                                workout_sessions: sessionsRes.error?.message || null,
                            },
                        });
                    } else {
                        console.warn("[restore] history recovery latest skipped", {
                            env: getRuntimeEnvironmentLabel(),
                            user_id: user.id,
                            reason: recoveryFetch.reason,
                            fallbackUsed: false,
                            currentDisplayed: getHistoryOverallMetrics(latestHistoryRef.current || {}),
                        });
                    }
                }

                const remoteHistory = applyHistoryDeleteMarkers(
                    buildRemoteHistoryWithWorkoutRowsPriority(
                        workoutsRes.data || [],
                        sessionsRes.error ? [] : (sessionsRes.data || [])
                    ),
                    effectiveDeleteMarkers
                );
                const workoutsOnlyHistory = applyHistoryDeleteMarkers(
                    buildHistoryFromWorkoutRows(workoutsRes.data || []),
                    effectiveDeleteMarkers
                );
                const hasRemoteWorkout = getValidWorkoutDatesFromHistory(remoteHistory).length > 0;
                const fetchedWorkoutRowsCount = (workoutsRes.data || []).length;
                const fetchedSessionRowsCount = sessionsRes.error ? 0 : (sessionsRes.data || []).length;
                const currentDisplayedBeforeApply = latestHistoryRef.current || {};

                if (!hasRemoteWorkout && (fetchedWorkoutRowsCount > 0 || fetchedSessionRowsCount > 0)) {
                    const canShowWorkoutsData = getValidWorkoutDatesFromHistory(workoutsOnlyHistory).length > 0;
                    console.warn("[restore] fetched rows did not produce history; keeping current state", {
                        env: getRuntimeEnvironmentLabel(),
                        fetchName: "history_initial_load",
                        user_id: user.id,
                        workoutsRowsCount: fetchedWorkoutRowsCount,
                        workoutSessionsRowsCount: fetchedSessionRowsCount,
                        workouts: getWorkoutRowsDebugSummary(workoutsRes.data || []),
                        workout_sessions: sessionsRes.error
                            ? { rowCount: 0, error: sessionsRes.error?.message || String(sessionsRes.error) }
                            : getWorkoutSessionRowsDebugSummary(sessionsRes.data || []),
                        currentDisplayed: getHistoryOverallMetrics(currentDisplayedBeforeApply),
                        fallbackUsed: false,
                        emptyHistoryApplied: false,
                        workoutsDataFallbackCanDisplay: canShowWorkoutsData,
                        reason: "Supabase rows exist but could not be converted to valid history",
                    });
                    if (isActive && canShowWorkoutsData) {
                        historyRemoteLoadFailedRef.current = false;
                        historyHasTrustedRemoteSnapshotRef.current = true;
                        setHistoryRemoteReady(true);
                        setHistoryLoadError("");
                        applyTrustedWorkoutRowsSnapshot(workoutsRes.data || [], {
                            source: "history_initial_load conversion fallback",
                        });
                        if (!sessionsRes.error && !sessionsRes.skipped) {
                            applyTrustedSessionRowsSnapshot(sessionsRes.data || [], {
                                source: "history_initial_load conversion fallback",
                            });
                        }
                        applyWorkoutsDataHistorySnapshot(workoutsOnlyHistory, {
                            source: "workouts.data",
                            reason: "history_initial_load conversion fallback",
                            requestId: "initial-load",
                            weekRange: getCurrentWeekRangeForHomeSummary(),
                            workoutsHistory: workoutsOnlyHistory,
                        });
                        console.log("[restore] workouts.data applied despite session conversion issue", {
                            env: getRuntimeEnvironmentLabel(),
                            user_id: user.id,
                            historyRemoteReady: true,
                            remoteLoadFailed: false,
                            workoutsRowsCount: fetchedWorkoutRowsCount,
                            workoutSessionsRowsCount: fetchedSessionRowsCount,
                            history: getHistoryOverallMetrics(latestHistoryRef.current || {}),
                            workoutsDataHistory: getHistoryOverallMetrics(workoutsOnlyHistory),
                            appliedDates: getValidWorkoutDatesFromHistory(workoutsOnlyHistory),
                        });
                        return;
                    }
                    setHistoryLoadError("記録を読み込めませんでした");
                    return;
                }

                if (!hasRemoteWorkout && !historyHasTrustedRemoteSnapshotRef.current) {
                    console.warn("[restore] no remote workout rows found; empty history not persisted as cache", {
                        env: getRuntimeEnvironmentLabel(),
                        fetchName: "history_initial_load",
                        user_id: user.id,
                        workoutsRowsCount: fetchedWorkoutRowsCount,
                        workoutSessionsRowsCount: fetchedSessionRowsCount,
                        currentDisplayed: getHistoryOverallMetrics(currentDisplayedBeforeApply),
                        fallbackUsed: false,
                    });
                }

                let mergedHistory = remoteHistory;
                const appliedWorkoutsDataHistory = workoutsOnlyHistory;
                const currentWeekRange = getCurrentWeekRangeForHomeSummary();
                markSupabaseFetchFresh(`display_history:history:${user.id}:${currentWeekRange.start}:${currentWeekRange.end}:${INITIAL_HOME_HISTORY_LIMIT}`, 45000);
                const initialSummaryHistory = sessionsRes.error || sessionsRes.skipped
                    ? {}
                    : buildHistoryFromWorkoutSessionRows(sessionsRes.data || []);
                const initialWorkoutsDataApplied = applyWorkoutsDataHistorySnapshot(appliedWorkoutsDataHistory, {
                    source: "workouts.data",
                    reason: "history_initial_load",
                    requestId: "initial-load",
                    weekRange: currentWeekRange,
                    workoutsHistory: appliedWorkoutsDataHistory,
                    summaryHistory: initialSummaryHistory,
                });

                console.log("[restore] Supabase priority load", {
                    env: getRuntimeEnvironmentLabel(),
                    user_id: user.id,
                    source: hasRemoteWorkout ? "supabase" : "supabase_empty",
                    fallbackUsed: false,
                    fallbackSource: "localStorage/history_cache",
                    fallbackRejected: true,
                    rejectedReason: "logged-in initial load uses Supabase only",
                    dateRange: { from: sessionRangeStart, limit: initialHistoryLimit },
                    workoutsRows: workoutsRes.data?.length || 0,
                    sessionRows: sessionsRes.data?.length || 0,
                    supabaseDates: getValidWorkoutDatesFromHistory(remoteHistory),
                    localStorageDates: getValidWorkoutDatesFromHistory(localMergeCandidate),
                    appliedDates: getValidWorkoutDatesFromHistory(mergedHistory),
                });
                if (shouldLogPerfDebug()) {
                    console.log("[home weekly summary] apply", {
                        source: "workouts.data",
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        requestId: "initial-load",
                        applied: initialWorkoutsDataApplied,
                        reason: "history_initial_load",
                        ...getHomeWeeklySummaryDebug(appliedWorkoutsDataHistory),
                        ...getHomeWeeklySourceDebug({
                            workoutsHistory: appliedWorkoutsDataHistory,
                            summaryHistory: initialSummaryHistory,
                            finalHistory: appliedWorkoutsDataHistory,
                            weekRange: currentWeekRange,
                            source: "workouts.data",
                            appliedSource: initialWorkoutsDataApplied ? "workouts.data" : "none",
                            ignoredStaleSource: sessionsRes.skipped
                                ? "summary_json skipped on initial load"
                                : "summary_json ignored for home weekly",
                        }),
                    });
                }

                if (!isActive) return;

                historyRemoteLoadFailedRef.current = false;
                historyHasTrustedRemoteSnapshotRef.current = true;
                applyTrustedWorkoutRowsSnapshot(workoutsRes.data || [], {
                    source: "history_initial_load",
                });
                if (!sessionsRes.error && !sessionsRes.skipped) {
                    applyTrustedSessionRowsSnapshot(sessionsRes.data || [], {
                        source: "history_initial_load",
                    });
                }
                // ── Startup reconciliation ────────────────────────────────
                // Run BEFORE setHistory/persistHistoryForUser so that:
                // 1. localMergeCandidate still has local-only dates (not yet overwritten)
                // 2. unsyncedDates' data can be merged into mergedHistory before it's
                //    applied as the canonical state — this ensures latestHistoryRef.current
                //    has the data when useHistoryAutoSave tries to upsert it
                if (onStartupUnsyncedLocalDates && startupReconcileForUserRef.current !== user.id) {
                    startupReconcileForUserRef.current = user.id;
                    const since = getDateDaysAgoKey(INITIAL_HOME_HISTORY_LOOKBACK_DAYS);
                    const localDates = getValidWorkoutDatesFromHistory(localMergeCandidate, { since });
                    const remoteDateSet = new Set(
                        (workoutsRes.data || []).map((r) => String(r?.date || "").slice(0, 10)).filter(Boolean)
                    );
                    // F-3: persistentDeletedDates filter removed; outbox guards against re-uploading deletes.
                    const unsyncedDates = localDates.filter(
                        (d) => !remoteDateSet.has(d)
                    );
                    // F-3: no longer filtered by persistentDeletedDates
                    const deletedDatesExcluded = [];
                    if (deletedDatesExcluded.length > 0) {
                        console.log("[WP] startupReconcile:deletedDatesExcluded", {
                            excludedDates: deletedDatesExcluded,
                            unsyncedDates,
                        });
                    }
                    console.log("[startup reconcile] local vs remote comparison", {
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        localDatesInWindow: localDates.length,
                        remoteDatesInWindow: remoteDateSet.size,
                        unsyncedDates,
                        deletedDatesExcluded,
                    });
                    if (unsyncedDates.length > 0) {
                        // Merge local-only dates into mergedHistory so that:
                        // - setHistory() shows them on the calendar immediately
                        // - persistHistoryForUser() doesn't erase them from localStorage
                        // - latestHistoryRef.current has the data for useHistoryAutoSave
                        mergedHistory = applyLocalHistoryDates(mergedHistory, localMergeCandidate, unsyncedDates);
                        onStartupUnsyncedLocalDates(unsyncedDates);
                    }
                }
                setHistoryRemoteReady(true);
                setHistoryLoadError("");
                setHistoryDisplayFromCache(false);
                setHistory(mergedHistory);
                console.log("[restore] history ready state applied", {
                    env: getRuntimeEnvironmentLabel(),
                    user_id: user.id,
                    historyRemoteReady: true,
                    remoteLoadFailed: false,
                    workoutsRowsCount: fetchedWorkoutRowsCount,
                    workoutSessionsRowsCount: fetchedSessionRowsCount,
                    history: getHistoryOverallMetrics(mergedHistory),
                    workoutsDataHistory: getHistoryOverallMetrics(appliedWorkoutsDataHistory),
                    appliedDates: getValidWorkoutDatesFromHistory(mergedHistory),
                });
                // Track earliest authoritatively-loaded date for the bounded cache wrapper.
                if (!historyLoadedRangeStartRef.current || sessionRangeStart < historyLoadedRangeStartRef.current) {
                    historyLoadedRangeStartRef.current = sessionRangeStart;
                }
                if (hasRemoteWorkout || mergedHistory !== remoteHistory) {
                    persistHistoryForUser(user.id, mergedHistory, {
                        rangeFrom: sessionRangeStart,
                        rangeTo: null,
                        source: "history_initial_load",
                    });
                }
            } catch (error) {
                console.error("history sync load failed", {
                    error,
                    message: error?.message,
                    code: error?.code,
                    details: error?.details,
                    hint: error?.hint,
                    userId: user.id,
                    email: user.email,
                });

                if (!isActive) return;

                historyRemoteLoadFailedRef.current = true;
                setHistoryRemoteReady(false);
                setHistoryLoadError(classifyNetworkError(error));
                const fallbackHistory = applyHistoryDeleteMarkers(localMergeCandidate, effectiveDeleteMarkers);
                const currentDisplayedHistory = latestHistoryRef.current || {};
                const hasCurrentData = getHistoryOverallMetrics(currentDisplayedHistory).setCount > 0;
                const hasFallbackData = getHistoryOverallMetrics(fallbackHistory).setCount > 0;
                // Apply the persisted history cache as offline fallback only when:
                //   1. No Supabase snapshot was loaded this session (startup offline, not mid-session loss)
                //   2. Nothing is currently displayed — history state is still empty
                //   3. The cache actually contains workout data
                // Conditions 1+2 together prevent an out-of-date cache from clobbering a richer in-memory
                // state that was built from a previous successful fetch during the same app session.
                const canApplyFallback = hasFallbackData && !hasCurrentData && !historyHasTrustedRemoteSnapshotRef.current;
                const fallbackMetrics = getHistoryOverallMetrics(fallbackHistory);
                let cacheBytesForTrace = 0;
                try { cacheBytesForTrace = window.localStorage.getItem(getUserHistoryCacheKey(user.id))?.length || 0; } catch {}
                const fallbackRejectedReason = hasCurrentData
                    ? "hasCurrentData — stale cache must not overwrite current displayed state"
                    : historyHasTrustedRemoteSnapshotRef.current
                        ? "historyHasTrustedRemoteSnapshotRef — trusted remote snapshot exists"
                        : "fallback cache has no workout data";
                logWriteTrace("offlineFallback:apply", {
                    date: null,
                    decision: canApplyFallback ? "executed" : "skip",
                    reason: canApplyFallback ? "offline fallback applied from history_cache" : fallbackRejectedReason,
                    extra: {
                        cacheKey: getUserHistoryCacheKey(user.id),
                        cacheBytes: cacheBytesForTrace,
                        cacheExerciseCount: fallbackMetrics.exerciseCount,
                        cacheSetCount: fallbackMetrics.setCount,
                        cacheDateCount: getValidWorkoutDatesFromHistory(fallbackHistory).length,
                        remoteLoadFailed: historyRemoteLoadFailedRef.current,
                        historyRemoteReady: false,
                        hasCurrentData,
                        offlineEarlyExit: Boolean(error?.__offlineEarlyExit),
                    },
                });
                if (canApplyFallback) {
                    setHistory(fallbackHistory);
                    setHistoryDisplayFromCache(true);
                    applyWorkoutsDataHistorySnapshot(fallbackHistory, {
                        allowRegression: true,
                        source: "localStorage/history_cache",
                        reason: "offline startup fallback",
                        requestId: "offline-fallback",
                    });
                    console.log("[restore] offline fallback applied from history cache", {
                        env: getRuntimeEnvironmentLabel(),
                        fetchName: "history_initial_load",
                        user_id: user.id,
                        fallbackSource: "localStorage/history_cache",
                        fallback: fallbackMetrics,
                        errorMessage: error?.message || String(error || "unknown error"),
                        offlineEarlyExit: Boolean(error?.__offlineEarlyExit),
                    });
                } else {
                    // Only reset historyDisplayFromCache when trusted remote data exists or
                    // there is genuinely nothing to show. If hasCurrentData is true but
                    // historyHasTrustedRemoteSnapshotRef is false, the currently-displayed data
                    // came from the offline cache (a previous run of this same effect set it via
                    // setHistoryDisplayFromCache(true) + setHistory(fallbackHistory)). Resetting
                    // to false here would blank the banner text even though cache data is showing.
                    if (!hasCurrentData || historyHasTrustedRemoteSnapshotRef.current) {
                        setHistoryDisplayFromCache(false);
                    }
                    console.warn("[restore] fallback rejected after Supabase history load failure", {
                        env: getRuntimeEnvironmentLabel(),
                        fetchName: "history_initial_load",
                        user_id: user.id,
                        dateRange: { from: getDateDaysAgoKey(INITIAL_HOME_HISTORY_LOOKBACK_DAYS), limit: INITIAL_HOME_HISTORY_LIMIT },
                        query: "workouts.select(date,data).eq(user_id).gte(date).order(date asc).limit",
                        errorMessage: error?.message || String(error || "unknown error"),
                        fallbackUsed: false,
                        fallbackSource: "localStorage/history_cache",
                        fallback: fallbackMetrics,
                        currentDisplayed: getHistoryOverallMetrics(currentDisplayedHistory),
                        fallbackRejected: true,
                        rejectedReason: fallbackRejectedReason,
                    });
                }
            } finally {
                if (isActive) setHistorySyncReady(true);
            }
        };

        syncHistoryFromSupabase();

        return () => {
            isActive = false;
        };
    }, [
        applyTrustedSessionRowsSnapshot,
        applyTrustedWorkoutRowsSnapshot,
        applyWorkoutsDataHistorySnapshot,
        getCurrentHistoryDeleteMarkers,
        historyReloadNonce,
        markSupabaseFetchFresh,
        runDedupeSupabaseFetch,
        user,
        HISTORY_OWNER_KEY,
        applyHistoryDeleteMarkers,
        applyLocalHistoryDates,
        attachRecordFetchContext,
        buildHistoryFromWorkoutSessionRows,
        buildRemoteHistoryWithWorkoutRowsPriority,
        buildVersionedHistoryCache,
        createEmptyHistoryDeleteMarkers,
        getCurrentWeekRangeForHomeSummary,
        getDateDaysAgoKey,
        getHistoryDeleteMarkersKey,
        classifyNetworkError,
        getHistoryOverallMetrics,
        getHomeWeeklySourceDebug,
        getHomeWeeklySummaryDebug,
        getRuntimeEnvironmentLabel,
        getUserHistoryCacheKey,
        getWorkoutRowsDebugSummary,
        getWorkoutSessionRowsDebugSummary,
        historyDeleteMarkersRef,
        isPlainObject,
        latestHistoryRef,
        loadTrustedHistoryCache,
        logRecordFetchError,
        normalizeHistoryDeleteMarkers,
        persistHistoryForUser,
        setHistory,
        setWorkoutsDataHistory,
        shouldLogPerfDebug,
        workoutsDataHistoryRef,
        isPro,
        onStartupUnsyncedLocalDates,
    ]);

    // ─── refreshDisplayHistoryFromSupabase effect ─────────────────────────────
    useEffect(() => {
        if (!user?.id || !historySyncReady) return;
        if (!["home", "calendar", "analytics", "log", "history"].includes(screen)) return;
        const trustedDisplayHistory = (workoutsDataHistory || {});
        const trustedHistoryMetrics = getHistoryOverallMetrics(trustedDisplayHistory);
        if (trustedHistoryMetrics.setCount > 0 && !["analytics", "home", "log", "history", "calendar"].includes(screen)) {
            console.log("[home fetch] display_history skipped; trustedHistory already available", {
                env: getRuntimeEnvironmentLabel(),
                user_id: user.id,
                screen,
                source: "trustedHistory",
                applied: false,
                skipped: true,
                reason: "screen uses existing trusted history immediately",
                trustedHistory: trustedHistoryMetrics,
            });
            return;
        }
        if (trustedHistoryMetrics.setCount > 0 && ["analytics", "home", "log", "history"].includes(screen)) {
            console.log("[home fetch] display_history background refresh for analytics PR", {
                env: getRuntimeEnvironmentLabel(),
                user_id: user.id,
                screen,
                source: "trustedHistory",
                applied: false,
                skipped: false,
                reason: "analytics PR refreshes workouts.data so stale PR/cache/manual data cannot remain authoritative",
                trustedHistory: trustedHistoryMetrics,
            });
        }

        let cancelled = false;

        const refreshDisplayHistoryFromSupabase = async () => {
            if (typeof navigator !== "undefined" && navigator.onLine === false) return;
            const requestId = displayHistoryRefreshRequestIdRef.current + 1;
            displayHistoryRefreshRequestIdRef.current = requestId;
            const queryLabel = "display_history_refresh";
            const weekRange = getCurrentWeekRangeForHomeSummary();
            const rawSessionRangeStart = getDateDaysAgoKey(
                screen === "calendar" ? REMOTE_HISTORY_SESSION_LOOKBACK_DAYS : 120
            );
            const sessionRangeStart = clampStartForFreeTier(rawSessionRangeStart, isPro);
            const sessionRangeEnd = null;
            const displayHistoryLimit = screen === "calendar" ? REMOTE_HISTORY_SESSION_LIMIT : 180;

            try {
                let workoutsQuery = supabase
                    .from("workouts")
                    .select("date, data")
                    .eq("user_id", user.id)
                    .gte("date", sessionRangeStart);
                let sessionsQuery = supabase
                    .from("workout_sessions")
                    .select("workout_date, duration_sec, summary_json")
                    .eq("user_id", user.id)
                    .gte("workout_date", sessionRangeStart);

                if (sessionRangeEnd) {
                    workoutsQuery = workoutsQuery.lt("date", sessionRangeEnd);
                    sessionsQuery = sessionsQuery.lt("workout_date", sessionRangeEnd);
                }

                // DESC order ensures the most recent dates are always returned
                // when the total row count exceeds the limit.
                // buildHistoryFromWorkoutRows sorts internally so order doesn't matter there.
                const ascendingOrder = screen === "calendar";
                workoutsQuery = workoutsQuery
                    .order("date", { ascending: ascendingOrder })
                    .limit(displayHistoryLimit);
                sessionsQuery = sessionsQuery
                    .order("workout_date", { ascending: ascendingOrder })
                    .limit(displayHistoryLimit);

                const displayFetchKey = `display_history:${screen}:${user.id}:${sessionRangeStart}:${sessionRangeEnd || "open"}:${displayHistoryLimit}`;
                console.log("[refreshDisplay] about to runDedupeSupabaseFetch", {
                    screen,
                    displayFetchKey,
                    isFresh: isSupabaseFetchFresh(displayFetchKey),
                });
                const displayFetch = await runDedupeSupabaseFetch(
                    displayFetchKey,
                    async () => {
                        const [workoutsRes, sessionsRes] = await Promise.all([
                            workoutsQuery,
                            sessionsQuery,
                        ]);
                        if (workoutsRes.error) {
                            const context = logRecordFetchError(queryLabel, "workouts", workoutsRes.error, {
                                userId: user.id,
                                dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: displayHistoryLimit },
                                query: "workouts.select(date,data).eq(user_id).gte(date).lte(date?).order(date asc).limit",
                                responseData: workoutsRes.data,
                            });
                            throw attachRecordFetchContext(workoutsRes.error, context);
                        }
                        return { workoutsRes, sessionsRes };
                    },
                    {
                        minIntervalMs: 20000,
                        freshTtlMs: 30000,
                        backoffMs: 30000,
                        context: {
                            fetchName: queryLabel,
                            user_id: user.id,
                            screen,
                            dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: displayHistoryLimit },
                            tables: ["workouts", "workout_sessions"],
                        },
                    }
                );

                if (displayFetch.skipped) {
                    console.log("[restore] display history fetch skipped", {
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        screen,
                        requestId,
                        applied: false,
                        reason: displayFetch.reason,
                        dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: displayHistoryLimit },
                        currentDisplayed: getHistoryOverallMetrics(latestHistoryRef.current || {}),
                        currentWorkoutsDataDisplayed: getHistoryOverallMetrics(workoutsDataHistoryRef.current || {}),
                    });
                    return;
                }

                const { workoutsRes, sessionsRes } = displayFetch.value;

                // ── 6/13 specific diagnostic ──────────────────────────────
                const _debugDate = "2026-06-13";
                const _debugRow = (workoutsRes.data || []).find((r) => String(r?.date || "").slice(0, 10) === _debugDate);
                if (_debugRow) {
                    const _debugHistory = buildHistoryFromWorkoutRows([_debugRow]);
                    const _debugExercises = Object.entries(_debugHistory || {}).map(([name, records]) => ({
                        name,
                        recordCount: records.length,
                        firstRecordSets: records[0]?.sets || [],
                        firstRecordDate: records[0]?.date,
                    }));
                    console.log("[refreshDisplay] 6/13 row found", {
                        rawRow: { date: _debugRow.date, dataKeys: Object.keys(_debugRow.data || {}) },
                        builtExercises: _debugExercises,
                        isInHistory: _debugExercises.length > 0,
                    });
                } else {
                    console.warn("[refreshDisplay] 6/13 row NOT in workoutsRes.data", {
                        totalRows: workoutsRes.data?.length,
                        dates: (workoutsRes.data || []).map((r) => r?.date).filter(Boolean).sort().slice(-10),
                    });
                }
                // ─────────────────────────────────────────────────────────

                if (sessionsRes.error) {
                    logRecordFetchError(queryLabel, "workout_sessions", sessionsRes.error, {
                        userId: user.id,
                        dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: displayHistoryLimit },
                        query: "workout_sessions.select(workout_date,duration_sec,summary_json).eq(user_id).gte(workout_date).lte(workout_date?).order(workout_date asc).limit",
                        responseData: sessionsRes.data,
                    });
                    console.warn("[restore] workout_sessions display summary refresh failed; using workouts.data", {
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        table: "workout_sessions",
                        query: "workout_sessions.select(workout_date,duration_sec,summary_json).eq(user_id).gte(workout_date).order(workout_date asc).limit",
                        code: sessionsRes.error?.code,
                        message: sessionsRes.error?.message,
                        details: sessionsRes.error?.details,
                        hint: sessionsRes.error?.hint,
                        responseData: sessionsRes.data,
                    });
                }

                if (cancelled || displayHistoryRefreshRequestIdRef.current !== requestId) {
                    if (shouldLogPerfDebug()) console.log("[home weekly summary] ignore stale", {
                        source: sessionsRes.error ? "workouts.data" : "workouts.data + workout_sessions.summary_json",
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        requestId,
                        latestRequestId: displayHistoryRefreshRequestIdRef.current,
                        applied: false,
                        reason: cancelled ? "cancelled" : "stale display history request",
                        ...getHomeWeeklySummaryDebug(buildHistoryFromWorkoutRows(workoutsRes.data || []), weekRange),
                    });
                    return;
                }

                const effectiveDeleteMarkers = getCurrentHistoryDeleteMarkers();
                const workoutsOnlyHistory = applyHistoryDeleteMarkers(
                    buildHistoryFromWorkoutRows(workoutsRes.data || []),
                    effectiveDeleteMarkers
                );
                const remoteHistory = applyHistoryDeleteMarkers(
                    buildRemoteHistoryWithWorkoutRowsPriority(
                        workoutsRes.data || [],
                        sessionsRes.error ? [] : (sessionsRes.data || [])
                    ),
                    effectiveDeleteMarkers
                );
                const _allPendingLocalDates = Array.from(pendingWorkoutContentChangeDatesRef.current.keys());
                const _persistentDeletedForDisplay = loadPersistentDeletedDates(user.id);
                const _excludedFromPending = _allPendingLocalDates.filter((d) => d in _persistentDeletedForDisplay);
                const pendingLocalDates = new Set(_allPendingLocalDates.filter((d) => !(d in _persistentDeletedForDisplay)));
                if (_excludedFromPending.length > 0) {
                    console.log("[WP] displayRefresh:pendingLocalDatesFiltered", {
                        screen,
                        excludedDates: _excludedFromPending,
                        pendingLocalDates: [...pendingLocalDates],
                    });
                }
                const remoteDates = getValidWorkoutDatesFromHistory(remoteHistory)
                    .filter((date) => !pendingLocalDates.has(date));
                const workoutsDates = getValidWorkoutDatesFromHistory(workoutsOnlyHistory)
                    .filter((date) => !pendingLocalDates.has(date));
                console.log("[refreshDisplay] dates after filter", {
                    screen,
                    workoutsRowsReturned: workoutsRes.data?.length || 0,
                    allRemoteDates: getValidWorkoutDatesFromHistory(remoteHistory),
                    remoteDatesAfterFilter: remoteDates,
                    pendingLocalDates: [...pendingLocalDates],
                    earlyReturn: !remoteDates.length && !workoutsDates.length,
                });
                if (!remoteDates.length && !workoutsDates.length) return;

                const currentHistory = latestHistoryRef.current || {};
                let nextHistory = applyHistoryDeleteMarkers(
                    applyLocalHistoryDates(currentHistory, remoteHistory, remoteDates),
                    effectiveDeleteMarkers
                );
                let nextWorkoutsDataHistory = applyHistoryDeleteMarkers(
                    applyLocalHistoryDates(
                        workoutsDataHistoryRef.current || currentHistory,
                        workoutsOnlyHistory,
                        workoutsDates
                    ),
                    effectiveDeleteMarkers
                );

                // 永続削除リストに載っている日付を nextHistory / nextWorkoutsDataHistory から除去する。
                // 目的: 表示リフレッシュの Supabase フェッチが delete より前に走った場合、
                //       リモートがまだ削除前のデータを返し applyLocalHistoryDates が
                //       その日付を re-insert してしまうのを防ぐ。
                //
                // strip 条件: 永続リストに載っており、かつ以下のいずれか:
                //   (i)  保留中の変更が存在しない（同期完了後）
                //   (ii) 保留中の変更が日付全体の削除（explicitDelete:true & deleteScope:"date"）
                //
                // strip しない（skip）条件:
                //   保留中の変更がユーザーの編集（例: explicitEdit）。
                //   これにより「削除後に同じ日付へ記録を作り直した」ケースを保護する。
                {
                    const _datesToStrip = [];
                    const _skippedDates = [];
                    for (const d of Object.keys(_persistentDeletedForDisplay)) {
                        const _pendingChange = pendingWorkoutContentChangeDatesRef.current.get(d);
                        if (!_pendingChange) {
                            // (i) 保留なし → strip
                            _datesToStrip.push(d);
                        } else if (_pendingChange.explicitDelete === true && _pendingChange.deleteScope === "date") {
                            // (ii) 日付全体の削除が pending → strip
                            _datesToStrip.push(d);
                        } else {
                            // ユーザー編集が pending → skip
                            _skippedDates.push({
                                date: d,
                                reason: _pendingChange.explicitEdit ? "pending-user-edit" : "pending-other",
                            });
                        }
                    }
                    if (_datesToStrip.length > 0) {
                        for (const d of _datesToStrip) {
                            nextHistory = removeHistoryDateFromMap(nextHistory, d);
                            nextWorkoutsDataHistory = removeHistoryDateFromMap(nextWorkoutsDataHistory, d);
                        }
                    }
                    if (_datesToStrip.length > 0 || _skippedDates.length > 0) {
                        console.log("[WP] displayRefresh:deletedDatesRemovedFromHistory", {
                            screen,
                            removedDates: _datesToStrip,
                            skippedDates: _skippedDates,
                        });
                    }
                }

                console.log("[restore] display history refreshed from workouts.data priority", {
                    env: getRuntimeEnvironmentLabel(),
                    user_id: user.id,
                    screen,
                    workoutsRows: workoutsRes.data?.length || 0,
                    sessionRows: sessionsRes.error ? 0 : (sessionsRes.data?.length || 0),
                    appliedDates: remoteDates,
                    skippedPendingLocalDates: Array.from(pendingLocalDates),
                    exerciseNamesByDate: remoteDates.reduce((acc, date) => {
                        acc[date] = getHistoryMetricsForDate(nextHistory, date).exerciseNames;
                        return acc;
                    }, {}),
                });

                historyHasTrustedRemoteSnapshotRef.current = true;
                historyRemoteLoadFailedRef.current = false;
                applyTrustedWorkoutRowsSnapshot(workoutsRes.data || [], {
                    source: "display_history_refresh",
                });
                if (!sessionsRes.error) {
                    applyTrustedSessionRowsSnapshot(sessionsRes.data || [], {
                        source: "display_history_refresh",
                    });
                }
                // Track earliest authoritatively-loaded date for the bounded cache wrapper.
                if (!historyLoadedRangeStartRef.current || sessionRangeStart < historyLoadedRangeStartRef.current) {
                    historyLoadedRangeStartRef.current = sessionRangeStart;
                }
                if (serializeHistoryMap(nextHistory) !== serializeHistoryMap(currentHistory)) {
                    setHistory(nextHistory);
                    persistHistoryForUser(user.id, nextHistory, {
                        rangeFrom: sessionRangeStart,
                        rangeTo: sessionRangeEnd,
                        source: "display_history_refresh",
                    });
                }
                const displayWorkoutsDataApplied = applyWorkoutsDataHistorySnapshot(nextWorkoutsDataHistory, {
                    source: "workouts.data",
                    reason: "display refresh workouts.data canonical",
                    requestId,
                    weekRange,
                    workoutsHistory: workoutsOnlyHistory,
                    summaryHistory: sessionsRes.error ? {} : buildHistoryFromWorkoutSessionRows(sessionsRes.data || []),
                });
                if (shouldLogPerfDebug()) {
                    console.log("[home weekly summary] apply", {
                        source: "workouts.data",
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        requestId,
                        applied: displayWorkoutsDataApplied,
                        reason: "display refresh workouts.data canonical",
                        ...getHomeWeeklySummaryDebug(nextWorkoutsDataHistory, weekRange),
                        ...getHomeWeeklySourceDebug({
                            workoutsHistory: workoutsOnlyHistory,
                            summaryHistory: sessionsRes.error ? {} : buildHistoryFromWorkoutSessionRows(sessionsRes.data || []),
                            finalHistory: nextWorkoutsDataHistory,
                            weekRange,
                            source: "workouts.data",
                            appliedSource: displayWorkoutsDataApplied ? "workouts.data" : "none",
                            ignoredStaleSource: displayWorkoutsDataApplied ? "summary_json/history_cache/localStorage" : "stale display history refresh",
                        }),
                    });
                }
                setHistoryLoadError("");
            } catch (error) {
                if (cancelled) return;
                console.error("[restore] display history refresh failed", {
                    env: getRuntimeEnvironmentLabel(),
                    fetchName: queryLabel,
                    user_id: user.id,
                    screen,
                    dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: displayHistoryLimit },
                    table: error?.recordFetch?.table || "workouts",
                    query: error?.recordFetch?.query || "workouts.select(date,data).eq(user_id).gte(date).lte(date?).order(date asc).limit",
                    code: error?.code || null,
                    message: error?.message || String(error || "unknown error"),
                    details: error?.details || null,
                    hint: error?.hint || null,
                    responseData: error?.recordFetch?.responseData || null,
                    fallbackUsed: false,
                    fallbackSource: "none",
                    fallbackRejected: true,
                    rejectedReason: "display refresh keeps current displayed state on fetch failure",
                    currentDisplayed: getHistoryOverallMetrics(latestHistoryRef.current || {}),
                    currentWorkoutsDataDisplayed: getHistoryOverallMetrics(workoutsDataHistoryRef.current || {}),
                });
                setHistoryLoadError(classifyNetworkError(error));
            }
        };

        refreshDisplayHistoryFromSupabase();

        return () => {
            cancelled = true;
        };
    }, [
        applyTrustedSessionRowsSnapshot,
        applyTrustedWorkoutRowsSnapshot,
        applyWorkoutsDataHistorySnapshot,
        applyLocalHistoryDates,
        getCurrentHistoryDeleteMarkers,
        historySyncReady,
        historyReloadNonce,
        runDedupeSupabaseFetch,
        screen,
        sessionSyncVersion,
        user?.id,
        workoutsDataHistory,
        applyHistoryDeleteMarkers,
        attachRecordFetchContext,
        buildHistoryFromWorkoutSessionRows,
        buildRemoteHistoryWithWorkoutRowsPriority,
        getCurrentWeekRangeForHomeSummary,
        getDateDaysAgoKey,
        classifyNetworkError,
        getHistoryMetricsForDate,
        getHistoryOverallMetrics,
        getHomeWeeklySourceDebug,
        getHomeWeeklySummaryDebug,
        getNextMonthPrefix,
        getRuntimeEnvironmentLabel,
        latestHistoryRef,
        logRecordFetchError,
        pendingWorkoutContentChangeDatesRef,
        persistHistoryForUser,
        serializeHistoryMap,
        setHistory,
        shouldLogPerfDebug,
        workoutsDataHistoryRef,
        isPro,
        isSupabaseFetchFresh,
    ]);

    // ─── home weekly summary effect ────────────────────────────────────────────
    useEffect(() => {
        if (!user?.id || !historySyncReady || screen !== "history") return;

        let cancelled = false;
        const requestId = homeWeeklySummaryRequestIdRef.current + 1;
        homeWeeklySummaryRequestIdRef.current = requestId;
        const weekRange = getCurrentWeekRangeForHomeSummary();

        const refreshHomeWeeklySummaryFromWorkouts = async () => {
            if (typeof navigator !== "undefined" && navigator.onLine === false) return;
            const sessionRangeStart = weekRange.start;
            const sessionRangeEnd = weekRange.end;
            const homeHistoryLimit = INITIAL_HOME_HISTORY_LIMIT;
            const homeFetchKey = `home_weekly:${user.id}:${sessionRangeStart}:${sessionRangeEnd}`;

            try {
                const homeFetch = await runDedupeSupabaseFetch(
                    homeFetchKey,
                    async () => {
                        const result = await supabase
                            .from("workouts")
                            .select("date, data")
                            .eq("user_id", user.id)
                            .gte("date", sessionRangeStart)
                            .lte("date", sessionRangeEnd)
                            .order("date", { ascending: true })
                            .limit(homeHistoryLimit);
                        if (result.error) {
                            logRecordFetchError("home_weekly_summary_refresh", "workouts", result.error, {
                                userId: user.id,
                                dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: homeHistoryLimit },
                                query: "workouts.select(date,data).eq(user_id).gte(date).lte(date).order(date asc).limit",
                                responseData: result.data,
                            });
                            throw result.error;
                        }
                        return result;
                    },
                    {
                        minIntervalMs: 20000,
                        freshTtlMs: 30000,
                        backoffMs: 30000,
                        context: {
                            fetchName: "home_weekly_summary_refresh",
                            user_id: user.id,
                            dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: homeHistoryLimit },
                            table: "workouts",
                        },
                    }
                );

                if (homeFetch.skipped) {
                    const currentWorkoutsDataHistory = workoutsDataHistoryRef.current || {};
                    console.log("[home weekly summary] fetch skipped", {
                        source: "workouts.data",
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        requestId,
                        applied: false,
                        reason: homeFetch.reason,
                        dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: homeHistoryLimit },
                        currentDisplayed: getHistoryOverallMetrics(currentWorkoutsDataHistory),
                        ...getHomeWeeklySummaryDebug(currentWorkoutsDataHistory, weekRange),
                        ...getHomeWeeklySourceDebug({
                            workoutsHistory: currentWorkoutsDataHistory,
                            finalHistory: currentWorkoutsDataHistory,
                            weekRange,
                            source: "workouts.data",
                            appliedSource: "current trusted workoutsDataHistory",
                            ignoredStaleSource: `fetch skipped: ${homeFetch.reason || "unknown"}`,
                        }),
                    });
                    return;
                }

                const { data } = homeFetch.value;
                applyTrustedWorkoutRowsSnapshot(data || [], {
                    source: "home_weekly_summary_refresh",
                });

                const remoteWorkoutsHistory = applyHistoryDeleteMarkers(
                    buildHistoryFromWorkoutRows(data || []),
                    getCurrentHistoryDeleteMarkers()
                );
                const _allPendingLocalDatesWeekly = Array.from(pendingWorkoutContentChangeDatesRef.current.keys());
                const _persistentDeletedForWeekly = loadPersistentDeletedDates(user.id);
                const _excludedFromPendingWeekly = _allPendingLocalDatesWeekly.filter((d) => d in _persistentDeletedForWeekly);
                const pendingLocalDates = new Set(_allPendingLocalDatesWeekly.filter((d) => !(d in _persistentDeletedForWeekly)));
                if (_excludedFromPendingWeekly.length > 0) {
                    console.log("[WP] displayRefresh:pendingLocalDatesFiltered", {
                        screen: "home_weekly",
                        excludedDates: _excludedFromPendingWeekly,
                        pendingLocalDates: [...pendingLocalDates],
                    });
                }
                const remoteDates = getValidWorkoutDatesFromHistory(remoteWorkoutsHistory)
                    .filter((date) => !pendingLocalDates.has(date));

                const nextWorkoutsDataHistory = applyLocalHistoryDates(
                    workoutsDataHistoryRef.current || latestHistoryRef.current || {},
                    remoteWorkoutsHistory,
                    remoteDates
                );

                if (cancelled || homeWeeklySummaryRequestIdRef.current !== requestId) {
                    if (shouldLogPerfDebug()) console.log("[home weekly summary] ignore stale", {
                        source: "workouts.data",
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        requestId,
                        latestRequestId: homeWeeklySummaryRequestIdRef.current,
                        applied: false,
                        reason: cancelled ? "cancelled" : "stale home weekly request",
                        ...getHomeWeeklySummaryDebug(nextWorkoutsDataHistory, weekRange),
                        ...getHomeWeeklySourceDebug({
                            workoutsHistory: remoteWorkoutsHistory,
                            finalHistory: nextWorkoutsDataHistory,
                            weekRange,
                            source: "workouts.data",
                            appliedSource: "none",
                            ignoredStaleSource: cancelled ? "cancelled" : "stale workouts.data response",
                        }),
                    });
                    return;
                }

                const homeWeeklyApplied = applyWorkoutsDataHistorySnapshot(nextWorkoutsDataHistory, {
                    source: "workouts.data",
                    reason: "home screen workouts.data refresh",
                    requestId,
                    weekRange,
                    workoutsHistory: remoteWorkoutsHistory,
                });
                historyHasTrustedRemoteSnapshotRef.current = true;
                historyRemoteLoadFailedRef.current = false;
                setHistoryLoadError("");

                if (shouldLogPerfDebug()) {
                    console.log("[home weekly summary] apply", {
                        source: "workouts.data",
                        env: getRuntimeEnvironmentLabel(),
                        user_id: user.id,
                        requestId,
                        applied: homeWeeklyApplied,
                        reason: "home screen workouts.data refresh",
                        ...getHomeWeeklySummaryDebug(nextWorkoutsDataHistory, weekRange),
                        ...getHomeWeeklySourceDebug({
                            workoutsHistory: remoteWorkoutsHistory,
                            finalHistory: nextWorkoutsDataHistory,
                            weekRange,
                            source: "workouts.data",
                            appliedSource: homeWeeklyApplied ? "workouts.data" : "none",
                            ignoredStaleSource: homeWeeklyApplied
                                ? "summary_json/history_cache/localStorage"
                                : "stale home_weekly_summary_refresh",
                        }),
                    });
                }
            } catch (error) {
                if (cancelled) return;
                console.error("[home weekly summary] refresh failed", {
                    source: "workouts.data",
                    env: getRuntimeEnvironmentLabel(),
                    user_id: user.id,
                    requestId,
                    applied: false,
                    reason: "workouts.data fetch failed",
                    dateRange: { from: sessionRangeStart, to: sessionRangeEnd, limit: homeHistoryLimit },
                    query: "workouts.select(date,data).eq(user_id).gte(date).lte(date).order(date asc).limit",
                    fallbackUsed: false,
                    fallbackSource: "none",
                    fallbackRejected: true,
                    rejectedReason: "home weekly keeps current workoutsDataHistory on fetch failure",
                    currentDisplayed: getHistoryOverallMetrics(workoutsDataHistoryRef.current || {}),
                    code: error?.code || null,
                    message: error?.message || String(error || "unknown error"),
                    details: error?.details || null,
                    hint: error?.hint || null,
                });
            }
        };

        refreshHomeWeeklySummaryFromWorkouts();

        return () => {
            cancelled = true;
        };
    }, [
        applyTrustedWorkoutRowsSnapshot,
        applyWorkoutsDataHistorySnapshot,
        applyLocalHistoryDates,
        getCurrentHistoryDeleteMarkers,
        historyReloadNonce,
        historySyncReady,
        runDedupeSupabaseFetch,
        screen,
        sessionSyncVersion,
        user?.id,
        applyHistoryDeleteMarkers,
        getCurrentWeekRangeForHomeSummary,
        getHistoryOverallMetrics,
        getHomeWeeklySourceDebug,
        getHomeWeeklySummaryDebug,
        getRuntimeEnvironmentLabel,
        latestHistoryRef,
        logRecordFetchError,
        pendingWorkoutContentChangeDatesRef,
        shouldLogPerfDebug,
        workoutsDataHistoryRef,
    ]);

    // ─── Return ───────────────────────────────────────────────────────────────
    return {
        trustedWorkoutRows,
        trustedSessionRows,
        historyRemoteReady,
        historyDisplayFromCache,
        historyLoadError,
        setHistoryLoadError,
        historySyncReady,
        historyRemoteLoadFailedRef,
        historyLoadedRangeStartRef,
        trustedWorkoutRowsRef,
        applyTrustedWorkoutRowsSnapshot,
        applyTrustedSessionRowsSnapshot,
        markSupabaseFetchFresh,
        isSupabaseFetchFresh,
        invalidateSupabaseFetchFreshByPrefix,
        runDedupeSupabaseFetch,
    };
}

export {
    REMOTE_HISTORY_SESSION_LOOKBACK_DAYS,
    REMOTE_HISTORY_SESSION_LIMIT,
    INITIAL_HOME_HISTORY_LOOKBACK_DAYS,
    INITIAL_HOME_HISTORY_LIMIT,
    HISTORY_RECOVERY_LIMIT,
};
